import {
  createErrorResponse,
  getQueryParamValue,
} from '@@/api/mock-server/utils.js'
import InvoiceReportsTempData from '@@/app/config/invoice-reports-mock.json'
import { InvoiceReportPaymentStatus } from '@/models/reports/invoiceReport.js'
import { USER_WORK_ROLES } from '@/models/users.js'
import type { MirageServer, MockServer } from '@@/api/mock-server/server.js'
import type { InvoiceReportListResponse } from '@/composables/api/InvoiceReport.js'
import { toNumber } from '@/utils/number.js'

function applyFilters(
  this: MirageServer,
  invoicesData: typeof InvoiceReportsTempData.data,
  params: Record<string, unknown>,
) {
  let filteredData = [...invoicesData]

  // Filter by client
  const clientFilter = getQueryParamValue(params['filter[client_id.in]'])
  if (clientFilter) {
    const clientIds = clientFilter.split(',').map((id) => parseInt(id))
    filteredData = filteredData.filter((invoice) =>
      clientIds.includes(invoice.attributes.client_id),
    )
  }

  // Filter by client isnull
  const clientIsNullFilter = getQueryParamValue(
    params['filter[client_id.isnull]'],
  )
  if (clientIsNullFilter === 'true') {
    filteredData = filteredData.filter(
      (invoice) =>
        invoice.attributes.client_id === null ||
        invoice.attributes.client_id === undefined,
    )
  }

  // Filter by department
  const departmentFilter = getQueryParamValue(
    params['filter[department_id.in]'],
  )
  if (departmentFilter) {
    const departmentValues = departmentFilter.split(',')
    const departmentNames: string[] = []

    departmentValues.forEach((value) => {
      // Check if value is numeric (department ID) or string (department name)
      const isNumeric = /^\d+$/.test(value)

      if (isNumeric) {
        // Look up department name by ID from schema
        const department = this.schema.find('department', value)
        if (department?.attrs.name) {
          departmentNames.push(department.attrs.name)
        }
      } else {
        // Value is already a department name
        departmentNames.push(value)
      }
    })

    filteredData = filteredData.filter((invoice) =>
      departmentNames.some(
        (name) =>
          name.toLowerCase() ===
          invoice.attributes.department_name?.toLowerCase(),
      ),
    )
  }

  // Filter by supervising veterinarian
  const veterinarianFilter = getQueryParamValue(
    params['filter[supervising_vet_id_from_invoice.in]'],
  )
  if (veterinarianFilter) {
    const veterinarianValues = veterinarianFilter.split(',')
    const veterinarianNames: string[] = []

    veterinarianValues.forEach((value) => {
      // Check if value is numeric (vet ID) or string (vet name)
      const isNumeric = /^\d+$/.test(value)

      if (isNumeric) {
        // Look up vet name by ID from schema
        const user = this.schema.find('user', value)
        if (user?.attrs) {
          const firstName = user.attrs.first_name || ''
          const lastName = user.attrs.last_name || ''
          const fullName = `${firstName} ${lastName}`.trim()
          if (fullName) {
            veterinarianNames.push(fullName)
          }
        }
      } else {
        // Value is already a vet name
        veterinarianNames.push(value)
      }
    })

    filteredData = filteredData.filter((invoice) =>
      veterinarianNames.some(
        (name) =>
          name.toLowerCase() ===
          invoice.attributes.supervising_vet_name_from_invoice?.toLowerCase(),
      ),
    )
  }

  // Filter by invoice type
  const invoiceTypeFilter = getQueryParamValue(
    params['filter[invoice_type.in]'],
  )
  if (invoiceTypeFilter) {
    const invoiceTypes = invoiceTypeFilter.split(',')
    filteredData = filteredData.filter((invoice) =>
      invoiceTypes.includes(invoice.attributes.invoice_type),
    )
  }

  // Filter by payment status
  const paymentStatusFilter = getQueryParamValue(
    params['filter[payment_status.in]'],
  )
  if (paymentStatusFilter) {
    const paymentStatuses = paymentStatusFilter.split(',')
    filteredData = filteredData.filter((invoice) => {
      const amountDue = toNumber(invoice.attributes.amount_due)
      const totalInclVat = toNumber(invoice.attributes.total_incl_vat)

      const isInCredit = amountDue < 0
      const isPaid = amountDue === 0
      const isUnpaid = amountDue === totalInclVat && totalInclVat > 0
      const isPartiallyPaid = amountDue > 0 && amountDue < totalInclVat

      return Boolean(
        (paymentStatuses.includes(InvoiceReportPaymentStatus.InCredit) &&
          isInCredit) ||
        (paymentStatuses.includes(InvoiceReportPaymentStatus.Paid) && isPaid) ||
        (paymentStatuses.includes(InvoiceReportPaymentStatus.PartiallyPaid) &&
          isPartiallyPaid) ||
        (paymentStatuses.includes(InvoiceReportPaymentStatus.Unpaid) &&
          isUnpaid),
      )
    })
  }

  // Filter by invoice status
  const invoiceStatusFilter = getQueryParamValue(
    params['filter[invoice_status.in]'],
  )
  if (invoiceStatusFilter) {
    const invoiceStatuses = invoiceStatusFilter.split(',')
    filteredData = filteredData.filter((invoice) =>
      invoiceStatuses.includes(invoice.attributes.invoice_status),
    )
  }

  // Filter by payment method
  const paymentMethodFilter = getQueryParamValue(
    params['filter[payment_method.in]'],
  )
  if (paymentMethodFilter) {
    // Note: payment_method might not exist in the temp data
    // This is a placeholder that will work when the field is available
    const paymentMethods = paymentMethodFilter.split(',')
    filteredData = filteredData.filter((invoice) => {
      // @ts-expect-error - payment_method might not exist in type
      return invoice.attributes.payment_method
        ? // @ts-expect-error - payment_method might not exist in type
          paymentMethods.includes(invoice.attributes.payment_method)
        : false
    })
  }

  // Filter by insurance claim status
  const insuranceClaimStatusFilter = getQueryParamValue(
    params['filter[insurance_claim_status.in]'],
  )
  if (insuranceClaimStatusFilter) {
    // Note: insurance_claim_status might not exist in the temp data
    // This is a placeholder that will work when the field is available
    const claimStatuses = insuranceClaimStatusFilter.split(',')
    filteredData = filteredData.filter((invoice) => {
      // @ts-expect-error - insurance_claim_status might not exist in type
      return invoice.attributes.insurance_claim_status
        ? claimStatuses.includes(
            // @ts-expect-error - insurance_claim_status might not exist in type
            invoice.attributes.insurance_claim_status,
          )
        : false
    })
  }

  // Filter by tags
  const tagsFilter = getQueryParamValue(params['filter[tags.in]'])
  if (tagsFilter) {
    // Note: tags might not exist in the temp data
    // This is a placeholder that will work when the field is available
    const tags = tagsFilter.split(',')
    filteredData = filteredData.filter((invoice) => {
      // @ts-expect-error - tags might not exist in type
      const invoiceTags = invoice.attributes.tags || []
      return tags.some((tag) => invoiceTags.includes(tag))
    })
  }

  // Filter by invoice tags
  const invoiceTagsFilter = getQueryParamValue(params['filter[invoice_tags]'])
  if (invoiceTagsFilter) {
    const tags = Array.isArray(invoiceTagsFilter)
      ? invoiceTagsFilter
      : invoiceTagsFilter.split(',')
    filteredData = filteredData.filter((invoice) => {
      const attrs = invoice.attributes as Record<string, unknown>
      const invoiceTags = (attrs.invoice_tags_list as string) || ''
      const tagsList = invoiceTags.split(',').map((t) => t.trim())
      return tags.some((tag) => tagsList.includes(tag))
    })
  }

  // Filter by client tags
  const clientTagsFilter = getQueryParamValue(params['filter[client_tags]'])
  if (clientTagsFilter) {
    const tags = Array.isArray(clientTagsFilter)
      ? clientTagsFilter
      : clientTagsFilter.split(',')
    filteredData = filteredData.filter((invoice) => {
      const attrs = invoice.attributes as Record<string, unknown>
      const clientTags = (attrs.client_tags_list as string) || ''
      const tagsList = clientTags.split(',').map((t) => t.trim())
      return tags.some((tag) => tagsList.includes(tag))
    })
  }

  // Filter by referring vet ID
  const referringVetFilter = getQueryParamValue(
    params['filter[referring_vet_id_from_consultation.in]'],
  )
  if (referringVetFilter) {
    const vetIds = (
      Array.isArray(referringVetFilter)
        ? referringVetFilter
        : referringVetFilter.split(',')
    ).map(Number)
    filteredData = filteredData.filter((invoice) => {
      const attrs = invoice.attributes as Record<string, unknown>
      const referringVetId = attrs.referring_vet_id_from_consultation as
        | number
        | null
      return referringVetId !== null && vetIds.includes(referringVetId)
    })
  }

  // Filter by invoice issue date range
  const invoiceIssueDateRange = getQueryParamValue(
    params['filter[invoice_date.range]'],
  )
  if (invoiceIssueDateRange) {
    const dateRange = Array.isArray(invoiceIssueDateRange)
      ? invoiceIssueDateRange
      : invoiceIssueDateRange.split(',')
    const [startDate, endDate] = dateRange

    if (startDate && endDate) {
      // Extract date portion (YYYY-MM-DD) if timestamp is provided
      const startDateOnly = startDate.includes('T')
        ? startDate.split('T')[0]
        : startDate
      const endDateOnly = endDate.includes('T')
        ? endDate.split('T')[0]
        : endDate

      const startISO = `${startDateOnly}T00:00:00.000Z`
      const endISO = `${endDateOnly}T23:59:59.999Z`

      filteredData = filteredData.filter((invoice) => {
        const invoiceDate = invoice.attributes.invoice_date
        return invoiceDate >= startISO && invoiceDate <= endISO
      })
    }
  }

  // Filter by invoice due date range
  const invoiceDueDateRange = getQueryParamValue(
    params['filter[invoice_due_date.range]'],
  )
  if (invoiceDueDateRange) {
    const dateRange = Array.isArray(invoiceDueDateRange)
      ? invoiceDueDateRange
      : invoiceDueDateRange.split(',')
    const [startDate, endDate] = dateRange

    if (startDate && endDate) {
      // Extract date portion (YYYY-MM-DD) if timestamp is provided
      const startDateOnly = startDate.includes('T')
        ? startDate.split('T')[0]
        : startDate
      const endDateOnly = endDate.includes('T')
        ? endDate.split('T')[0]
        : endDate

      const startISO = `${startDateOnly}T00:00:00.000Z`
      const endISO = `${endDateOnly}T23:59:59.999Z`

      filteredData = filteredData.filter((invoice) => {
        const invoiceDueDate = invoice.attributes.invoice_due_date
        if (!invoiceDueDate) {
          return false
        }
        return invoiceDueDate >= startISO && invoiceDueDate <= endISO
      })
    }
  }

  // Filter by search
  const searchFilter = getQueryParamValue(params['filter[search]'])
  if (searchFilter) {
    const searchTerm = searchFilter.toLowerCase()
    filteredData = filteredData.filter((invoice) => {
      const searchableFields = [
        invoice.attributes.client_name,
        String(invoice.attributes.invoice_number),
        invoice.attributes.supervising_vet_name_from_invoice,
        invoice.attributes.department_name,
      ]

      return searchableFields.some(
        (field) => field && field.toLowerCase().includes(searchTerm),
      )
    })
  }

  return filteredData
}

export function routes(server: MockServer) {
  server.get(
    '/reports_v2/invoices/filters',
    function (this: MirageServer, _schema, _request) {
      const invoicesData = InvoiceReportsTempData.data

      const invoiceTagsSet = new Set<string>()
      const clientTagsSet = new Set<string>()
      const referringVetMap = new Map<number, string>()

      const tagColors: Record<
        string,
        { background: string; foreground: string }
      > = {
        urgent: { background: '#d32f2f', foreground: '#ffffff' },
        VIP: { background: '#f9a825', foreground: '#000000' },
        emergency: { background: '#c62828', foreground: '#ffffff' },
        'follow-up': { background: '#1565c0', foreground: '#ffffff' },
        routine: { background: '#757575', foreground: '#ffffff' },
        'long-term': { background: '#2e7d32', foreground: '#ffffff' },
        insurance: { background: '#7b1fa2', foreground: '#ffffff' },
        'new-client': { background: '#0277bd', foreground: '#ffffff' },
      }

      for (const invoice of invoicesData) {
        const attrs = invoice.attributes as Record<string, unknown>
        const invoiceTags = attrs.invoice_tags_list as string | undefined
        if (invoiceTags) {
          invoiceTags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
            .forEach((t) => invoiceTagsSet.add(t))
        }
        const clientTags = attrs.client_tags_list as string | undefined
        if (clientTags) {
          clientTags
            .split(',')
            .map((t) => t.trim())
            .filter(Boolean)
            .forEach((t) => clientTagsSet.add(t))
        }
        const referringVetId = attrs.referring_vet_id_from_consultation as
          | number
          | null
        const referringVetName = attrs.referring_vet_name_from_consultation as
          | string
          | null
        if (referringVetId && referringVetName) {
          referringVetMap.set(referringVetId, referringVetName)
        }
      }

      return {
        data: {
          type: 'InvoiceFilters',
          id: '1',
          attributes: {
            invoice_tags: Array.from(invoiceTagsSet)
              .sort()
              .map((name) => ({
                name,
                background: tagColors[name]?.background ?? null,
                foreground: tagColors[name]?.foreground ?? null,
              })),
            client_tags: Array.from(clientTagsSet)
              .sort()
              .map((name) => ({
                name,
                background: tagColors[name]?.background ?? null,
                foreground: tagColors[name]?.foreground ?? null,
              })),
            referring_vet: Array.from(referringVetMap.entries())
              .sort((a, b) => a[1].localeCompare(b[1]))
              .map(([id, name]) => ({ id, name })),
          },
        },
      }
    },
    { timing: 200 },
  )

  server.get(
    '/reports_v2/invoices/groups',
    function (this: MirageServer, _schema, request) {
      const params = request.queryParams
      const groupByParam = getQueryParamValue(params.group_by)

      if (!groupByParam) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'group_by parameter is required' }],
        })
      }

      // Apply filters using shared function
      const filteredInvoices = applyFilters.call(
        this,
        InvoiceReportsTempData.data,
        params,
      )

      // Group the filtered data
      const grouped = filteredInvoices.reduce(
        (acc, invoice) => {
          const groupValue = String(
            invoice.attributes[
              groupByParam as keyof typeof invoice.attributes
            ] || '',
          )
          if (!acc[groupValue]) {
            acc[groupValue] = []
          }
          acc[groupValue].push(invoice)
          return acc
        },
        {} as Record<string, typeof filteredInvoices>,
      )

      // Create group objects with aggregations
      let groupsData = Object.entries(grouped).map(([groupValue, invoices]) => {
        const totalInclVat = invoices.reduce(
          (sum, inv) => sum + toNumber(inv.attributes.total_incl_vat),
          0,
        )
        const amountPaid = invoices.reduce(
          (sum, inv) => sum + toNumber(inv.attributes.amount_paid),
          0,
        )
        const amountDue = invoices.reduce(
          (sum, inv) => sum + toNumber(inv.attributes.amount_due),
          0,
        )

        // Get group_id based on the group_by field
        let groupId: number | null = null
        if (groupByParam === 'client_name' && invoices[0]) {
          groupId = invoices[0].attributes.client_id
        } else if (groupByParam === 'department_name' && invoices[0]) {
          // Look up department ID by name from schema
          const departmentName = groupValue
          const department = this.schema.findBy('department', {
            name: departmentName,
          })
          groupId = department?.id ? parseInt(department.id) : null
        }

        return {
          type: 'InvoiceReportGroup' as const,
          id: `${groupByParam}:${groupValue || 'null'}`,
          attributes: {
            group_by: groupByParam,
            group_value: groupValue || null,
            group_id: groupId,
            count: invoices.length,
            total_incl_vat: totalInclVat,
            amount_paid: amountPaid,
            amount_due: amountDue,
          },
        }
      })

      // Apply sorting to groups
      const sortParam = getQueryParamValue(params.sort)
      if (sortParam) {
        const sortFields = Array.isArray(sortParam) ? sortParam : [sortParam]
        const sortField = sortFields[0]
        const isAscending = !sortField.startsWith('-')
        const sortKey = sortField.replace(/^-/, '')

        groupsData = [...groupsData].sort((a, b) => {
          let valueA: string | number | null = null
          let valueB: string | number | null = null

          if (sortKey === groupByParam) {
            valueA = a.attributes.group_value || ''
            valueB = b.attributes.group_value || ''
          } else if (sortKey === 'total_incl_vat') {
            valueA = a.attributes.total_incl_vat
            valueB = b.attributes.total_incl_vat
          } else if (sortKey === 'amount_paid') {
            valueA = a.attributes.amount_paid
            valueB = b.attributes.amount_paid
          } else if (sortKey === 'amount_due') {
            valueA = a.attributes.amount_due
            valueB = b.attributes.amount_due
          } else {
            return 0
          }

          // Compare values
          if (typeof valueA === 'string' && typeof valueB === 'string') {
            return isAscending
              ? valueA.localeCompare(valueB)
              : valueB.localeCompare(valueA)
          }

          const numA = toNumber(valueA)
          const numB = toNumber(valueB)
          return isAscending ? numA - numB : numB - numA
        })
      }

      // Apply pagination to groups
      const totalCount = groupsData.length
      const pageNumber = parseInt((params['page[number]'] as string) ?? '1')
      const pageSize = parseInt((params['page[size]'] as string) ?? '10')
      const totalPages = Math.ceil(totalCount / pageSize)

      const startIndex = (pageNumber - 1) * pageSize
      const endIndex = startIndex + pageSize

      if (startIndex >= totalCount && totalCount > 0) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: "Page doesn't exist" }],
        })
      }

      const paginatedGroups = groupsData.slice(startIndex, endIndex)

      return {
        data: paginatedGroups,
        meta: {
          pagination: {
            count: totalCount,
            page: pageNumber,
            pages: totalPages,
          },
        },
      }
    },
    { timing: 300 },
  )

  server.get(
    '/reports_v2/invoices',
    function (this: MirageServer, _schema, request) {
      return buildInvoiceReportListResponse.call(this, request)
    },
    { timing: 300 }, // Simulate API delay
  )
}

/**
 * Build list response for GET /reports_v2/invoices. Exported for tests.
 */
export function buildInvoiceReportListResponse(
  this: MirageServer,
  request: { queryParams: Record<string, unknown> },
) {
  // Apply filters using shared function
  let invoicesData = applyFilters.call(
    this,
    InvoiceReportsTempData.data,
    request.queryParams,
  )

  // Get query parameters
  const params = request.queryParams

  // Apply sorting
  const sortParam = getQueryParamValue(params.sort)
  if (sortParam) {
    const sortFields = Array.isArray(sortParam) ? sortParam : [sortParam]
    const sortField = sortFields[0]
    const isAscending = !sortField.startsWith('-')
    const sortKey = sortField.replace(/^-/, '')

    invoicesData = [...invoicesData].sort((a, b) => {
      let valueA: string | number | null = null
      let valueB: string | number | null = null

      switch (sortKey) {
        case 'consultation_id':
          valueA = a.attributes.consultation_id ?? 0
          valueB = b.attributes.consultation_id ?? 0
          break
        case 'invoice_type':
          valueA = a.attributes?.invoice_type || ''
          valueB = b.attributes?.invoice_type || ''
          break
        case 'patient_type':
          valueA = a.attributes?.patient_type || ''
          valueB = b.attributes?.patient_type || ''
          break
        case 'supervising_vet_name_from_invoice':
          valueA = a.attributes?.supervising_vet_name_from_invoice || ''
          valueB = b.attributes?.supervising_vet_name_from_invoice || ''
          break
        case 'department_name':
          valueA = a.attributes?.department_name || ''
          valueB = b.attributes?.department_name || ''
          break
        case 'client_name':
          valueA = a.attributes?.client_name || ''
          valueB = b.attributes?.client_name || ''
          break
        case 'invoice_date':
          valueA = a.attributes.invoice_date
          valueB = b.attributes.invoice_date
          break
        case 'invoice_number':
          valueA = a.attributes.invoice_number
          valueB = b.attributes.invoice_number
          break
        case 'invoice_status':
          valueA = a.attributes.invoice_status
          valueB = b.attributes.invoice_status
          break
        case 'total_incl_vat':
          valueA = a.attributes.total_incl_vat
          valueB = b.attributes.total_incl_vat
          break
        case 'amount_paid':
          valueA = a.attributes.amount_paid
          valueB = b.attributes.amount_paid
          break
        case 'amount_due':
          valueA = a.attributes.amount_due
          valueB = b.attributes.amount_due
          break
        default:
          return 0
      }

      // Compare values
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return isAscending
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA)
      }

      const numA = toNumber(valueA)
      const numB = toNumber(valueB)
      return isAscending ? numA - numB : numB - numA
    })
  }

  // Calculate aggregation from filtered data (before pagination)
  const totalCount = invoicesData.length
  const aggregation = invoicesData.reduce(
    (acc, invoice) => {
      const attrs = invoice.attributes
      const amountDue = toNumber(attrs.amount_due)
      const isInCredit = amountDue < 0
      return {
        total_amount: acc.total_amount + toNumber(attrs.total_incl_vat),
        total_paid: acc.total_paid + toNumber(attrs.amount_paid),
        total_due: acc.total_due + (isInCredit ? 0 : amountDue),
        total_credit: acc.total_credit + (isInCredit ? Math.abs(amountDue) : 0),
        total_in_credit: acc.total_in_credit + (isInCredit ? 1 : 0),
        fullyPaidCount:
          acc.fullyPaidCount +
          (Math.abs(
            toNumber(attrs.amount_paid) - toNumber(attrs.total_incl_vat),
          ) < 0.01
            ? 1
            : 0),
      }
    },
    {
      total_amount: 0,
      total_paid: 0,
      total_due: 0,
      total_credit: 0,
      total_in_credit: 0,
      fullyPaidCount: 0,
    },
  )

  const percentagePaid =
    totalCount > 0 ? (aggregation.fullyPaidCount / totalCount) * 100 : 0
  // Count-based: fraction of invoices that are in-credit
  const percentageInCredit =
    totalCount > 0 ? (aggregation.total_in_credit / totalCount) * 100 : 0
  // Monetary: credit amount as share of total amount
  const percentageCredit =
    aggregation.total_amount > 0
      ? (aggregation.total_credit / aggregation.total_amount) * 100
      : 0
  // Count-based: fraction of invoices that are outstanding (neither fully paid nor in credit)
  const percentageDue = Math.max(0, 100 - percentagePaid - percentageInCredit)

  // Apply pagination (validate to avoid NaN, 0, or negatives breaking indices)
  const rawPage = parseInt((params['page[number]'] as string) ?? '1', 10)
  const rawSize = parseInt((params['page[size]'] as string) ?? '10', 10)
  const pageNumber = !Number.isNaN(rawPage) && rawPage >= 1 ? rawPage : 1
  const pageSize =
    !Number.isNaN(rawSize) && rawSize >= 1 ? Math.min(rawSize, 100) : 10
  const totalPages = Math.ceil(totalCount / pageSize)

  const startIndex = (pageNumber - 1) * pageSize
  const endIndex = startIndex + pageSize

  if (startIndex >= totalCount && totalCount > 0) {
    return createErrorResponse({
      status: 404,
      errors: [{ detail: "Page doesn't exist" }],
    })
  }

  const paginatedData = invoicesData.slice(startIndex, endIndex)

  return {
    data: paginatedData as unknown as InvoiceReportListResponse['data'],
    meta: {
      pagination: {
        count: totalCount,
        page: pageNumber,
        pages: totalPages,
      },
      aggregation: {
        total_amount: aggregation.total_amount,
        total_paid: aggregation.total_paid,
        total_due: aggregation.total_due,
        total_credit: aggregation.total_credit,
        total_in_credit: aggregation.total_in_credit,
        percentage_collected: percentagePaid,
        percentage_outstanding: percentageDue,
        percentage_paid: percentagePaid,
        percentage_due: percentageDue,
        percentage_credit: percentageCredit,
        percentage_in_credit: percentageInCredit,
      },
    },
  }
}

export function seeds(server: MockServer) {
  void server.lazySeedingManager?.seedBasic('PaymentMethod')

  // Create specific veterinarians that match invoice-reports-mock.json names
  const specificVets = [
    { first_name: 'Teppo', last_name: 'Terävä' },
    { first_name: 'Janne', last_name: 'Huttunen' },
    { first_name: 'Jari', last_name: 'Nylander' },
    { first_name: 'Admin', last_name: 'User' },
    { first_name: 'Arto', last_name: 'Niittukari' },
    { first_name: 'Shane', last_name: 'Ali' },
    { first_name: 'Marja-Leena', last_name: 'Nurmela' },
    { first_name: 'Näim', last_name: 'Ahmad' },
    { first_name: 'Karlis', last_name: 'Daksis' },
    { first_name: 'Niels', last_name: 'Østergaard' },
  ]

  specificVets.forEach((vet) => {
    server.create('user', {
      ...vet,
      work_role: USER_WORK_ROLES.Veterinarian,
    })
  })
}
